import { useState } from 'react'
import { MapPin, TrendingUp, DollarSign, CheckCircle, Heart } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { useAppPath } from '../../hooks/useAppPath'
import { useAuth } from '../../contexts/AuthContext'
import { useMarketplaceFavorites } from '../../hooks/useMarketplaceFavorites'
import { DstListing } from '../../services/api/admin'
import { cn } from '../../lib/utils'
import {
  formatCentsCompact,
  formatReturnRange,
} from '../../lib/marketplaceFormatters'
import { SponsorMonogram } from './SponsorMonogram'

interface ListingCardProps {
  listing: DstListing
  onClick?: () => void
}

export function ListingCard({ listing, onClick }: ListingCardProps) {
  const navigate = useNavigate()
  const appPath = useAppPath()
  const { user } = useAuth()
  const { isFavorite, toggle } = useMarketplaceFavorites()
  const heroImage = listing.images[0] ?? null
  const location = [listing.addressCity, listing.addressState].filter(Boolean).join(', ') || '—'
  const sponsorLabel = (listing.sponsor ?? '').slice(0, 20) || null
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const favorited = isFavorite(listing.id)

  function handleClick() {
    if (onClick) {
      onClick()
    } else {
      navigate(appPath(`/marketplace/${listing.id}`))
    }
  }

  function handleFavoriteClick(event: React.MouseEvent) {
    event.stopPropagation()
    toggle(listing.id)
  }

  return (
    <div
      className={cn(
        'group cursor-pointer rounded-2xl overflow-hidden border border-border bg-card',
        'hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col'
      )}
    >
      <button
        type="button"
        className="text-left w-full flex flex-col flex-1"
        onClick={handleClick}
        aria-label={`View details for ${listing.title}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {heroImage && !imageLoadFailed ? (
            <img
              src={heroImage}
              alt={listing.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={() => setImageLoadFailed(true)}
            />
          ) : (
            <SponsorMonogram sponsor={listing.sponsor} />
          )}
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
            aria-hidden="true"
          />
          {sponsorLabel && (
            <span className="absolute top-3 left-3 bg-white/95 text-slate-900 rounded-full px-3 py-1 text-xs font-medium truncate max-w-[160px]">
              {sponsorLabel}
            </span>
          )}
          {listing.qualifiedStatus === 'qualified' && !user?.id && (
            <span className="absolute top-3 right-3 bg-emerald-500 text-white rounded-full px-2.5 py-1 text-xs font-semibold inline-flex items-center gap-1">
              <CheckCircle className="h-3 w-3" aria-hidden="true" />
              Qualified DST
            </span>
          )}
          {listing.qualifiedStatus === 'qualified' && user?.id && (
            <span className="absolute top-3 right-12 bg-emerald-500 text-white rounded-full px-2.5 py-1 text-xs font-semibold inline-flex items-center gap-1">
              <CheckCircle className="h-3 w-3" aria-hidden="true" />
              Qualified DST
            </span>
          )}
          {user?.id && (
            <button
              type="button"
              aria-label={favorited ? `Remove ${listing.title} from favorites` : `Save ${listing.title} to favorites`}
              onClick={handleFavoriteClick}
              className="absolute top-3 right-3 flex items-center justify-center w-7 h-7 rounded-full bg-white/90 shadow-sm transition-colors hover:bg-white"
            >
              <Heart
                className={cn(
                  'h-4 w-4',
                  favorited ? 'fill-rose-500 text-rose-500' : 'text-slate-600'
                )}
                aria-hidden="true"
              />
            </button>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="text-lg font-semibold text-white line-clamp-2 leading-snug">
              {listing.title}
            </h3>
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="h-3.5 w-3.5 text-white/80 shrink-0" aria-hidden="true" />
              <span className="text-sm text-white/80">{location}</span>
            </div>
          </div>
        </div>

        <div className="p-5 flex-1 flex flex-col">
          {(listing.dstAssetClass || listing.propertyType || listing.subProperties.length > 0) && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0 mb-3">
              {listing.dstAssetClass && (
                <span>
                  <span className="font-medium">Asset class:</span> {listing.dstAssetClass}
                </span>
              )}
              {listing.propertyType && (
                <span>
                  <span className="font-medium">Type:</span> {listing.propertyType.toUpperCase()}
                </span>
              )}
              {listing.subProperties.length > 0 && (
                <span>· {listing.subProperties.length} {listing.subProperties.length === 1 ? 'property' : 'properties'}</span>
              )}
            </div>
          )}
          <dl className="grid grid-cols-2 gap-3 mb-4 text-center">
            <div>
              <dt className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-0.5">
                <TrendingUp className="h-3 w-3" aria-hidden="true" />
                Return
              </dt>
              <dd className="text-base font-semibold text-emerald-600 dark:text-emerald-400">
                {formatReturnRange(listing.dstExpectedReturnMin, listing.dstExpectedReturnMax)}
              </dd>
            </div>
            <div>
              <dt className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-0.5">
                <DollarSign className="h-3 w-3" aria-hidden="true" />
                Min
              </dt>
              <dd className="text-base font-semibold text-foreground">
                {formatCentsCompact(listing.dstMinInvestmentCents)}
              </dd>
            </div>
          </dl>
        </div>
      </button>

      <div className="px-5 pb-5 pt-0">
        <Button
          className="w-full"
          onClick={handleClick}
          aria-label={`View details for ${listing.title}`}
        >
          View Details
        </Button>
      </div>
    </div>
  )
}
